// Route helper for the dataset-homed record view (design spec §6.3). Every
// SDR lives in exactly one dataset or one project folder, so its URL always
// says which; folder-homed records use projectFileViewerUrl
// (src/views/assistant/utils/projectFileRoute.ts) instead. Kept as a leaf
// module so citation plumbing (src/reusable/Chat) can build record links
// without importing the page and its data hooks.

/** The one dataset-homed record route. :source_uid has always been the SDR id. */
export const DATASET_DOCUMENT_ROUTE_PATH =
  "/datasets/:dataset_id/document/:source_uid";

export interface RecordViewOptions {
  /** Chunk to land on / scroll the record to (the cited passage). */
  chunkId?: number | string | null;
  /** 1-indexed page to open at. */
  page?: number | null;
  /** Intra-document search term to seed. */
  search?: string | null;
  /**
   * Project reference context (a record-reference id). Carried when the
   * record is opened from a project's file list so the view can offer the
   * Story-15 "Bring it in" repoint on its newer-revision notice.
   */
  ref?: number | null;
  /**
   * Collection the record was opened from (#2379). A collection record is
   * dataset-homed, so without this its Back affordance resolves to the
   * dataset curation viewer — an admin surface a read-only browser has no
   * business landing on.
   */
  fromCollection?: number | null;
  /**
   * On arrival, hand the record to the assistant chat (the chat-with-document
   * flow); the flag is consumed then stripped.
   */
  newChat?: boolean;
}

/** URL for one dataset-homed record, e.g. `/datasets/3/document/42?cid=7&page_number=3`. */
export const datasetDocumentViewerUrl = (
  datasetId: number,
  sdrId: number | string,
  {
    chunkId,
    page,
    search,
    ref,
    fromCollection,
    newChat,
  }: RecordViewOptions = {},
): string => {
  const params = new URLSearchParams();
  if (chunkId != null) params.set("cid", String(chunkId));
  if (page != null) params.set("page_number", String(page));
  if (search) params.set("search", search);
  if (ref != null) params.set("ref", String(ref));
  if (fromCollection != null)
    params.set("from_collection", String(fromCollection));
  if (newChat) params.set("new_chat", "1");
  const qs = params.toString();
  return `/datasets/${datasetId}/document/${sdrId}${qs ? `?${qs}` : ""}`;
};
